import { useState, useEffect, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { AlertTriangleIcon, LoaderIcon } from 'lucide-react'

interface DestructiveConfirmProps {
  /** Whether the dialog is open. */
  open: boolean
  /** Called when the user dismisses the dialog (backdrop click, ESC, X button). */
  onClose: () => void
  /** Dialog title. */
  title: string
  /** Dialog description / warning text. */
  description?: string
  /** Optional custom content between description and footer. */
  children?: ReactNode
  /** Label for the confirm button once the countdown completes. Default "Confirm". */
  confirmLabel?: string
  /** Called when the user clicks the active confirm button. */
  onConfirm: () => void
  /** Whether the action is in progress (shows spinner on button). */
  loading?: boolean
  /** Delay in seconds before the confirm button activates. Default 3. */
  delaySeconds?: number
  /** Button variant. Default "destructive". Use "default" for non-destructive confirmations. */
  variant?: 'destructive' | 'default'
}

/**
 * A confirmation dialog for destructive actions that requires the user to
 * wait a brief countdown before the confirm button becomes active.
 *
 * Prevents accidental clicks on permanent actions like suspending users,
 * dissolving organizations, GDPR-deleting accounts, force-paying cycles,
 * deleting plans, and cancelling requests.
 *
 * Supports a non-destructive variant for actions like reactivating a user.
 *
 * @example
 *   <DestructiveConfirm
 *     open={confirmDelete}
 *     onClose={() => setConfirmDelete(false)}
 *     title="Delete this item?"
 *     description="This action cannot be undone."
 *     confirmLabel="Delete"
 *     onConfirm={handleDelete}
 *     loading={deleting}
 *   />
 */
export function DestructiveConfirm({
  open,
  onClose,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  onConfirm,
  loading = false,
  delaySeconds = 3,
  variant = 'destructive',
}: DestructiveConfirmProps) {
  const [countdown, setCountdown] = useState(delaySeconds)

  // Reset countdown on open
  useEffect(() => {
    if (open) {
      setCountdown(delaySeconds)
    }
  }, [open, delaySeconds])

  // Countdown timer — starts once when dialog opens, runs until countdown hits 0
  useEffect(() => {
    if (!open) return
    setCountdown(delaySeconds)

    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(id)
  }, [open, delaySeconds])

  const canConfirm = countdown <= 0

  return (
    <Dialog open={open} onClose={onClose} title={title} description={description}>
      {children && <div className="mt-2">{children}</div>}

      {/* Countdown indicator */}
      {!canConfirm && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-amber-500/10 px-3 py-2 text-sm text-amber-600">
          <AlertTriangleIcon className="size-4 shrink-0" />
          <span>
            Confirm button enables in{' '}
            <strong>{countdown}s</strong> — review carefully before proceeding.
          </span>
        </div>
      )}

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant={variant}
          size="sm"
          onClick={onConfirm}
          disabled={!canConfirm || loading}
          className="gap-1.5 min-w-[100px]"
        >
          {loading ? (
            <LoaderIcon className="size-4 animate-spin" />
          ) : null}
          {canConfirm ? confirmLabel : `Confirm (${countdown}s)`}
        </Button>
      </div>
    </Dialog>
  )
}
